import { apiFetch } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'

const API_ROOT = import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'
const API_BASE = `${API_ROOT}/v1`

/** What the copy, create and save endpoints on the advertising chain require. */
const ADVERTISING_WRITE_PERMISSIONS = new Set([
  'MANAGE_ADVERTISING',
  'AX_ADVERTISER',
  'SELF_SERVICE_OWN_ADVERTISING',
])

export interface NetworkPermissionFlags {
  advertising: boolean
  /**
   * May change advertising, not only see it — a read-only advertiser or a
   * reports-only user has `advertising` but not this.
   */
  advertisingEditable: boolean
  publishing: boolean
  political: boolean
  data: boolean
  manageSystem: boolean
  /**
   * Ad ops: may manage advertising without being a marketplace advertiser. A UI
   * notion taken from the old UI's isAdOps; the API has no ad ops permission.
   */
  adOps: boolean
}

const PUBLISHER_PERMISSIONS = new Set(['AX_PUBLISHER', 'AX_PUBLISHER_ADMIN', 'AX_PUBLISHER_TEAM_ADMIN', 'MANAGE_PUBLISHING'])
const POLITICAL_TEAM_TYPES = new Set(['CONTROLLER', 'SPONSOR'])

/** One application's slice of a network's permissions, as the /user endpoint returns it. */
interface PermissionScope {
  networkPermissions?: string[] | null
  teamPermissions?: Record<string, string[] | null> | null
}

/** Whether any network- or team-level permission in the scope matches. */
function scopeHas(scope: PermissionScope | undefined, match: (permission: string) => boolean): boolean {
  if (!scope) return false
  const lists = [scope.networkPermissions, ...Object.values(scope.teamPermissions ?? {})]
  return lists.some((list) => Array.isArray(list) && list.some(match))
}

// Internal Adnuntius accounts. The API does not expose User.isInternalAdminUser,
// so match on username. These users bypass per-user module restrictions.
const ADNUNTIUS_ADMIN_USERNAMES = new Set(['adnuntius@adnuntius.com'])

/** A role the user holds in a network, through one of their teams. */
export interface UserNetworkRole {
  id: string
  name: string
  /** ADVERTISING, POLITICAL, DATA … */
  application?: string
}

export interface ApiUser {
  id: string
  posthogDistinctId?: string
  username: string
  name: string
  displayName: string
  locale: string
  concise: boolean
  /** Distinct roles per network, keyed by network tagId. */
  rolesByNetwork: Record<string, UserNetworkRole[]>
  isController: boolean
  /** Internal Adnuntius admin — sees every module regardless of granted permissions. */
  isAdnuntiusAdmin: boolean
  permissionsByNetwork: Record<string, NetworkPermissionFlags>
  preferredCurrency?: string
}

export interface UserUpdateInput {
  name?: string
  displayName?: string
  locale?: string
  concise?: boolean
}

export const UserApi = {
  getCurrent: async (): Promise<ApiUser> => {
    const url = new URL(`${API_BASE}/user`)
    url.searchParams.set('concise', 'true')
    url.searchParams.set('useNetworkTags', 'true')
    const res = await apiFetch(url, {
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) throw new Error(`Failed to fetch user (${res.status})`)
    const data = await res.json()
    const user = data.user ?? {}

    const apiRolesByNetwork: Array<{
      network: { id: string; tagId?: string }
      roles: Array<{ role?: { id: string; name?: string; application?: string } }>
    }> = user.userRoles?.rolesByNetwork ?? []

    // The same role can come through several teams; list it once per network.
    const rolesByNetwork: Record<string, UserNetworkRole[]> = {}
    for (const entry of apiRolesByNetwork) {
      const networkKey = entry.network?.tagId ?? entry.network?.id
      if (!networkKey) continue
      const roles = new Map<string, UserNetworkRole>()
      for (const { role } of entry.roles ?? []) {
        if (role?.id && !roles.has(role.id)) {
          roles.set(role.id, { id: role.id, name: role.name ?? role.id, application: role.application })
        }
      }
      rolesByNetwork[networkKey] = [...roles.values()]
    }

    const allPermissions: Record<string, Record<string, unknown>> = data.permissions ?? {}
    const isController = Object.values(allPermissions).some((network) =>
      Object.values(network).some((scope) => {
        if (!scope || typeof scope !== 'object') return false
        const tp = (scope as Record<string, unknown>).teamPermissions
        if (!tp || typeof tp !== 'object') return false
        return Object.values(tp as Record<string, string[]>).some((perms) =>
          Array.isArray(perms) && perms.includes('CONTROLLER')
        )
      })
    )

    // Derive per-network permission flags from the permissions object. The API
    // groups each permission under its application: the root scope is
    // ADVERTISING, with DATA and POLITICAL as nested scopes. Every network the
    // user is a member of has an entry, even one where they hold no permissions.
    const readOnlyTeams: Record<string, { teams?: Record<string, string> }> = data.readOnlyTeams ?? {}
    const permissionsByNetwork: Record<string, NetworkPermissionFlags> = {}
    for (const [networkId, networkPerms] of Object.entries(allPermissions)) {
      const advertising = networkPerms as PermissionScope
      const hasAdvertising = scopeHas(advertising, () => true)
      const canEditAdvertising = scopeHas(advertising, (p) => ADVERTISING_WRITE_PERMISSIONS.has(p))
      const hasPublishing = scopeHas(advertising, (p) => PUBLISHER_PERMISSIONS.has(p))
      const hasManageSystem = scopeHas(advertising, (p) => p === 'MANAGE_SYSTEM')
      const hasData = scopeHas(networkPerms.DATA as PermissionScope | undefined, () => true)
      const isAdOps =
        scopeHas(advertising, (p) => p === 'MANAGE_ADVERTISING') &&
        !scopeHas(advertising, (p) => p === 'AX_ADVERTISER')

      // Members of a controller or sponsor team on the permission-less
      // "Political Team Member Role" are reported under readOnlyTeams instead.
      const readOnlyPolitical = Object.values(readOnlyTeams[networkId]?.teams ?? {}).some((type) =>
        POLITICAL_TEAM_TYPES.has(type)
      )
      const hasPolitical =
        scopeHas(networkPerms.POLITICAL as PermissionScope | undefined, () => true) || readOnlyPolitical

      permissionsByNetwork[networkId] = {
        advertising: hasAdvertising,
        advertisingEditable: canEditAdvertising,
        publishing: hasPublishing,
        political: hasPolitical,
        data: hasData,
        manageSystem: hasManageSystem,
        adOps: isAdOps,
      }
    }

    const username: string = user.username ?? ''
    const stableUserId = user.id ?? data.userId
    const posthogDistinctId = typeof stableUserId === 'string' && stableUserId ? stableUserId : undefined

    return {
      id: stableUserId ?? username,
      posthogDistinctId,
      username,
      name: user.name ?? user.displayName ?? user.username ?? '',
      displayName: user.displayName ?? user.name ?? user.username ?? '',
      locale: user.locale ?? 'en_GB',
      concise: user.concise ?? false,
      rolesByNetwork,
      isController,
      isAdnuntiusAdmin: ADNUNTIUS_ADMIN_USERNAMES.has(username.toLowerCase()),
      permissionsByNetwork,
      preferredCurrency: (user.currency as string) || undefined,
    }
  },

  updateUser: async (userId: string, input: UserUpdateInput): Promise<void> => {
    const res = await apiFetch(`${API_BASE}/users/${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: userId, ...input }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to update user')
  },
}
